import React, { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { AppointmentContext } from "../context/AppointmentContext";
import { PulseLoader } from "react-spinners";
import config from "../config";
import { useNavigate } from "react-router-dom";
import { generateOneTimeToken } from "../utils/authToken";

const ExistingPatient = ({ doctor, onClose }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { hospitalName } = useContext(AppointmentContext);
  const navigate = useNavigate();

  const fetchPatients = async () => {
    const contact = sessionStorage.getItem("loginMobile");
    if (!contact) {
      navigate("/add-patient");
      return;
    }

    try {
      setLoading(true);
      const tokeng = generateOneTimeToken();
      const response = await axios.post(
        `${config.API_BASE_URL}/SELECT_ALL_UHID_BY_CONTACT_NUMBER_HPIMS_DB`,
        {
          TYPE: hospitalName || "default",
          CONTACT_NUMBER: contact,
        },
        {
          headers: { "X-API-Key": tokeng },
        }
      );

      const contactDetails = response.data?.data?.["contact-details"] || [];

      if (response.status === 200 && contactDetails.length > 0) {
        setPatients(contactDetails);
      } else {
        navigate("/add-patient");
      }
    } catch (err) {
      console.error("Error fetching patients:", err);
      setError("Unable to fetch patient data.");
      navigate("/add-patient");
    } finally {
      setLoading(false);
    }
  };

  const handlePatientClick = (patient) => {
    sessionStorage.setItem("selectedPatient", "divclick");
    sessionStorage.setItem("selectedUHID", patient?.UHID || "");
    navigate("/add-patient");
  };

  const handleAddNewClick = () => {
    sessionStorage.setItem("selectedPatient", "new");
    sessionStorage.removeItem("selectedUHID");
    navigate("/add-patient");
  };

  const filteredPatients = patients.filter((patient) =>
    patient?.PATIENT_NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient?.UHID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient?.CONTACT_NUMBER?.includes(searchTerm)
  );

  useEffect(() => {
    fetchPatients();
  }, []);

  return (
    <motion.div
      className="modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          background: "#fff",
          borderRadius: "12px",
          width: "95%",
          maxWidth: "800px",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <div
          className="bg-primary text-white fw-bold fs-5 px-4 py-3 border-bottom d-flex align-items-center justify-content-between"
        >
          <span role="heading" aria-level="2">
            👥 Choose from existing patients or add new
          </span>

          <button
            onClick={onClose}
            aria-label="Close patient selection modal"
            className="btn-close btn-close-white"
            style={{
              fontSize: "1.25rem",
              background: "none",
              border: "none",
              padding: "0",
              margin: "0",
              cursor: "pointer",
            }}
          >
            &times;
          </button>
        </div>


        {/* Body */}
        <div
          style={{
            padding: "20px 30px",
            flexGrow: 1,
            overflowY: "auto",
          }}
        >
          {/* Search + Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div style={{ flex: "1 1 300px", position: "relative" }}>
              <input
                type="text"
                placeholder="Search by name, UHID, or contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px 12px 44px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "12px",
                  fontSize: "14px",
                  background: "#f8fafc",
                  transition: "all 0.2s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#667eea";
                  e.target.style.background = "#ffffff";
                  e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.background = "#f8fafc";
                  e.target.style.boxShadow = "none";
                }}
              />
              <i
                className="fas fa-search"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "16px",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                  fontSize: "14px",
                }}
              ></i>
            </div>

            <button
              onClick={handleAddNewClick}
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "white",
                border: "none",
                borderRadius: "12px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 8px 20px rgba(16, 185, 129, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.3)";
              }}
            >
              <span style={{ fontSize: "16px" }}>➕</span>
              Add New Patient
            </button>
          </div>

          {/* List */}
          {loading ? (
            <div className="text-center py-5">
              <PulseLoader color="#6366f1" />
            </div>
          ) : error ? (
            <div className="text-danger text-center">{error}</div>
          ) : filteredPatients.length === 0 ? (
            <div className="text-muted text-center">No patients found.</div>
          ) : (
            filteredPatients.map((patient, index) => (
              <div
                key={index}
                className="mb-3 p-4 rounded border shadow-sm bg-light"
                style={{ cursor: "pointer" }}
                onClick={() => handlePatientClick(patient)}
              >
                <div className="fw-bold text-primary mb-3" style={{ fontSize: "16px" }}>
                  <i className="fas fa-arrow-circle-right me-2"></i>
                  {patient?.UHID || "Unknown UHID"}
                </div>

                <div
                  className="row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {/* Patient Name */}
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "6px",
                      }}
                    >
                      👤 Patient Name
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1e293b",
                        wordBreak: "break-word",
                      }}
                    >
                      {patient?.PATIENT_NAME || "N/A"}
                    </div>
                  </div>

                  {/* Guardian */}
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "6px",
                      }}
                    >
                      👨‍👩‍👧‍👦 Guardian
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1e293b",
                        wordBreak: "break-word",
                      }}
                    >
                      {patient?.RELATIVE_NAME || "N/A"}
                    </div>
                  </div>

                  {/* Contact */}
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "6px",
                      }}
                    >
                      📞 Contact
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#1e293b",
                      }}
                    >
                      {patient?.CONTACT_NUMBER || "N/A"}
                    </div>
                  </div>

                  {/* Address - full width */}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "6px",
                      }}
                    >
                      📍 Address
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#475569",
                        lineHeight: "1.5",
                        wordBreak: "break-word",
                      }}
                    >
                      {patient?.P_ADDRESS || "N/A"}
                    </div>
                  </div>
                </div>
              </div>

            ))
          )}
        </div>

        {/* Footer */}
        <div className="text-end border-top px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline-secondary rounded-xl"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ExistingPatient;
